'use client';

import { c as _c } from "react/compiler-runtime";
import type { Fetchable, MarketId } from '@morpho-org/blue-sdk';
import { MarketAvatar, SearchMenu, type SearchMenuContentProps } from '@morpho-org/ui-v6/client';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { getMarketName } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { Chevron } from '@repo/ui/icons';
import { memo, useMemo } from 'react';
import { useIsMedium } from '@/hooks/useBreakpoints';
import type { MarketForRow } from './config/useSelectMarketRows';
import { mobileSelectMarketColumns, selectMarketColumns, useSelectMarketRows } from './config';
export interface SelectMarketProps {
  isOpen: boolean;
  markets: Fetchable<MarketForRow[]>;
  selectedMarket?: MarketForRow;
  isLoading: boolean;
  buttonLabel?: string;
  side?: SearchMenuContentProps['side'];
  align?: SearchMenuContentProps['align'];
  direction?: 'row' | 'column';
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (marketId: MarketId) => void;
}
const SelectMarket = t0 => {
  const $ = _c(44);
  const {
    isOpen,
    isLoading,
    markets,
    selectedMarket,
    buttonLabel,
    side: t1,
    align: t2,
    direction: t3,
    onOpenChange,
    onSelect
  } = t0;
  const side = t1 === undefined ? "bottom" : t1;
  const align = t2 === undefined ? "end" : t2;
  const direction = t3 === undefined ? "column" : t3;
  const isTablet = useIsMedium();
  const t4 = selectedMarket?.marketId;
  let t5;
  if ($[0] !== markets || $[1] !== t4) {
    t5 = {
      markets,
      selectedMarketId: t4
    };
    $[0] = markets;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const rows = useSelectMarketRows(t5);
  const columns = isTablet ? mobileSelectMarketColumns : selectMarketColumns;
  let t6;
  bb0: {
    if (!selectedMarket) {
      t6 = "";
      break bb0;
    }
    const t7 = selectedMarket?.collateralAsset;
    const t8 = selectedMarket?.loanAsset;
    let t9;
    if ($[3] !== selectedMarket.lltv || $[4] !== t7 || $[5] !== t8) {
      t9 = getMarketName({
        collateralAsset: t7,
        loanAsset: t8,
        lltv: selectedMarket.lltv,
        maxLength: 10
      });
      $[3] = selectedMarket.lltv;
      $[4] = t7;
      $[5] = t8;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    t6 = t9;
  }
  const label = t6;
  if (isLoading) {
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="h-[57px] w-[150px] rounded-[20px]" />;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    return t7;
  }
  const t7 = `gap-xs flex flex-[1px] ${direction === "row" ? "flex-row items-center" : "flex-col items-start"}`;
  let t8;
  if ($[8] !== buttonLabel) {
    t8 = buttonLabel && <span className="text-3xs text-secondary-foreground">{buttonLabel}</span>;
    $[8] = buttonLabel;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isOpen || $[11] !== onOpenChange) {
    t9 = () => onOpenChange(!isOpen);
    $[10] = isOpen;
    $[11] = onOpenChange;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = selectedMarket?.collateralAsset?.logoURI;
  const t11 = selectedMarket?.loanAsset?.logoURI;
  let t12;
  if ($[13] !== t10 || $[14] !== t11) {
    t12 = <div className="gap-xs flex flex-row items-center"><MarketAvatar collateralAssetURI={t10} loanAssetURI={t11} /></div>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = isOpen ? "rotate(180deg)" : "rotate(0deg)";
  let t14;
  if ($[16] !== t13) {
    t14 = <Chevron style={{
      transition: "transform 0.2s ease-in-out",
      transform: t13
    }} />;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== isLoading || $[19] !== label || $[20] !== t12 || $[21] !== t14 || $[22] !== t9) {
    t15 = <SearchMenu.Trigger><Button variant="secondary" isLoading={isLoading} onClick={t9} data-testid="select-market-trigger">{t12}{label}{t14}</Button></SearchMenu.Trigger>;
    $[18] = isLoading;
    $[19] = label;
    $[20] = t12;
    $[21] = t14;
    $[22] = t9;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t15 || $[25] !== t7 || $[26] !== t8) {
    t16 = <div className={t7}>{t8}{t15}</div>;
    $[24] = t15;
    $[25] = t7;
    $[26] = t8;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = ["53%", "190px"];
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== onSelect) {
    t18 = row => onSelect(row.market.marketId);
    $[29] = onSelect;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== columns || $[32] !== rows || $[33] !== t18) {
    t19 = <SearchMenu.Table maxHeight={262} data={rows} columnsDefs={columns} columnWidths={t17} onRowClick={t18} />;
    $[31] = columns;
    $[32] = rows;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== align || $[36] !== side || $[37] !== t19) {
    t20 = <SearchMenu.Portal><SearchMenu.Content side={side} sideOffset={2} align={align} width="min(600px, 90vw)">{t19}</SearchMenu.Content></SearchMenu.Portal>;
    $[35] = align;
    $[36] = side;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== isOpen || $[40] !== onOpenChange || $[41] !== t16 || $[42] !== t20) {
    t21 = <SearchMenu.Root open={isOpen} onOpenChange={onOpenChange}>{t16}{t20}</SearchMenu.Root>;
    $[39] = isOpen;
    $[40] = onOpenChange;
    $[41] = t16;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  return t21;
};
export default memo(SelectMarket);